import { defineComponent as _defineComponent } from 'vue'

import type { MetaInfo } from 'vue-meta/types/vue-meta';

const __default__ = {
	name: 'TitleDetail',
	metaInfo() {
		return (this as unknown as { head: MetaInfo }).head;
	},
};

import type { Route } from 'vue-router';
import type { AssertByTypename } from '@/helpers/graphql-model-helper';
import type AppContent from '@/components/AppContent.vue';
import type { TitleDetail } from '@/interfaces/title-details-graphql';

import { diffInDays, diffInMilliseconds } from '@/helpers/date-helper';
import { TrackingHelper, transformContextToLinkToContext } from '@/helpers/tracking/tracking-helper';
import { TitleContentType } from '@/interfaces/snowplow/title-context';
import { SnowplowLinkContext } from '@/helpers/tracking/providers/snowplow-contexts';
import { useQuery } from '@/helpers/composables/useApollo';
import { useSendBidId } from '@/features/sponsored-recommendations/composables/useSendBidId';
import { useGetSrPlacement } from '@/features/sponsored-recommendations/composables/useGetSrPlacement';
import adminConfig from '@/features/promotions/config.json';
import { useTitleDetailInfo } from '@/pages/titleDetailV1/v1/composables/useTitleDetailInfo';

import { AdPlacement, MonetizationType, ObjectType, Platform, SraPlacement } from '@/@types/graphql-types';

import {
	GetUrlTitleDetailsDocument,
	GetUrlTitleDetailsQuery,
	GetUrlTitleDetailsQueryVariables,
} from '@/pages/graphql/queries/GetUrlTitleDetails.query';
import {
	GetNodeTitleDetailsDocument,
	GetNodeTitleDetailsQuery,
	GetNodeTitleDetailsQueryVariables,
} from '@/pages/graphql/queries/GetNodeTitleDetails.query';

import { ModalHelper } from '@/helpers/vfm/modal-helper';

import TitleDetailV1 from '@/pages/titleDetailV1/v1/TitleDetailV1.vue';
import SponsoredRecommendation from '@/features/sponsored-recommendations/components/SponsoredRecommendation.vue';
import Spinner from '@/ui-components/Spinner.vue';

import { captureMessageForSentry } from '@/helpers/sentry-helper';
import { getRichMetaDescriptions } from '@/components/experiments/TitleMetaDescriptions/ctrExperimentData';

import { useMounted } from '@vueuse/core';

import { useRoute } from 'vue-router/composables';
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { extractAVODOffer } from '@/helpers/title-helper';
import { generateUUIDv4 } from '@/helpers/uuid-helper';
import { useProvideInLists } from '@/helpers/composables/useProvideClickoutPlacement';

import { getJWTVQueryParams } from '@/helpers/jwtv-helper';
import { useJWTV } from '@/helpers/composables/useJwtv';
import { openJwTvRentalModal } from '@/features/jwtv-titles-rental/helpers/jwtv-titles-rental-helper';
import { getJWTVOffer } from '@/features/jwtv/config';

import { useTrackingStore } from '@/stores/pinia/useTrackingStore';
import { useMetaStore } from '@/stores/pinia/useMetaStore';
import { useConstantStore } from '@/stores/pinia/useConstantStore';
import { useLanguageStore } from '@/stores/pinia/useLanguageStore';
import { useUserStore } from '@/stores/pinia/useUserStore';
import { useRoutingStore } from '@/stores/pinia/useRoutingStore';
import { useSponsoredRecommendationsStore } from '@/stores/pinia/useSponsoredRecommendationsStore';

import { useTitleDetailsStore } from '@/stores/pinia/useTitleDetailsStore';
import { useAdServerStore } from '@/stores/pinia/useAdServerStore';
import { adServerCatalogueKey } from '@/features/ad-server/composables/useAdServer';
import { useMetaDescExp } from '@/components/experiments/MetaDescExp/useMetaDescExp';
import { getPromotedOffersWithOverride } from '@/features/buybox/helpers/promoted-offer-override-helper';


export default /*#__PURE__*/_defineComponent({
  ...__default__,
  props: {
    entityId: null
  },
  emits: ["closeTitleDetailModal"],
  setup(__props: any, { expose }: { emit: ({
	(e: 'closeTitleDetailModal'): void;
}), expose: any, slots: any, attrs: any }) {

const props = __props as { entityId?: string };

const PlexPlayerModal = () => import('@/ui-components/video-player-modals/PlexPlayerModal.vue');
const OTTPlayerModal = () => import('@/ui-components/video-player-modals/OttPlayerModal.vue');

const OldTitleDetail = () => import('@/pages/OldTitledetail.vue');

const EPISODE_MAX_LIMIT = 20;





const routingStore = useRoutingStore();
const metaStore = useMetaStore();
const constantStore = useConstantStore();
const languageStore = useLanguageStore();
const userStore = useUserStore();
const trackingStore = useTrackingStore();

const titleDetailStore = useTitleDetailsStore();
const {
	setSponsoredRecommendation,
	setTitleContext,
	clearTitleContext,
	setPermanentAudiences,
	resetPermanentAudiences,
	setLastVisitedTitleDetails,
	setInLists,
	clearInLists,
} = titleDetailStore;

const { clearResponse: clearAdServerResponse } = useAdServerStore();
const adServerKey = computed(() =>
	titleDetailNode.value?.id ? adServerCatalogueKey(AdPlacement.Buybox, titleDetailNode.value.id) : undefined
);

const route = useRoute();

const { clearAllSponsoredAds } = useSponsoredRecommendationsStore();

const content = ref<typeof AppContent | null>(null);
function scrollToTop() {
	content.value?.scrollToTop();
}

const showNewTitlePage = computed(() => {
	if (userStore.isLoggedIn) {
		return !userStore.showOldTitlePage;
	}

	return true;
});

const titleDetailInModal = computed(() => props.entityId != null);

// by fullpath
const titleDetailsFullPathQuery = useQuery<GetUrlTitleDetailsQuery, GetUrlTitleDetailsQueryVariables>(
	GetUrlTitleDetailsDocument,
	() => ({
		fullPath: route.path,
		site: routingStore.activeDomain,
		language: languageStore.language,
		country: languageStore.country,
		episodeMaxLimit: EPISODE_MAX_LIMIT,
		platform: Platform.Web,
		fallbackToForeignOffers: true,
	}),
	() => ({
		enabled: !titleDetailInModal.value && routingStore.activeRoute?.meta?.tab === 'detail',
		errorPolicy: 'all',
	})
);

titleDetailsFullPathQuery.onResult(result => {
	if (result.partial !== true) onTitleDetailNodeChange();

	if (result.errors && result.errors.length) {
		// report each partial error individually
		result.errors.forEach(err =>
			captureMessageForSentry(
				'[GraphQL Get Title Details By fullPath (partial)]:',
				{ error: err, where: 'Component: TitleDetail' },
				'error'
			)
		);
	}
});

titleDetailsFullPathQuery.onError(error => {
	captureMessageForSentry(
		'[GraphQL Get Title Details By fullPath]:',
		{ error: error, where: 'Component: TitleDetail' },
		'error'
	);
});

// by node id
const titleDetailsNodeQuery = useQuery<GetNodeTitleDetailsQuery, GetNodeTitleDetailsQueryVariables>(
	GetNodeTitleDetailsDocument,
	() =>
		({
			fullPath: route.path,
			entityId: props.entityId, // 'tm1141995', // rebel moon
			language: languageStore.language,
			country: languageStore.country,
			episodeMaxLimit: EPISODE_MAX_LIMIT,
			platform: Platform.Web,
			fallbackToForeignOffers: true,
		} as GetNodeTitleDetailsQueryVariables),
	() => ({ enabled: titleDetailInModal.value && !!props.entityId, errorPolicy: 'all' })
);

titleDetailsNodeQuery.onResult(result => {
	if (result.partial !== true) onTitleDetailNodeChange();

	if (result.errors && result.errors.length) {
		// report each partial error individually
		result.errors.forEach(err =>
			captureMessageForSentry(
				'[GraphQL Get Title Details By Node ID (partial)]:',
				{ error: err, where: 'Component: TitleDetail' },
				'error'
			)
		);
	}
});

titleDetailsNodeQuery.onError(error => {
	captureMessageForSentry(
		'[GraphQL Get Title Details By Node ID]:',
		{ error: error, where: 'Component: TitleDetail' },
		'error'
	);
});

const titleDetailNode = computed(() => {
	const node = titleDetailInModal.value
		? (titleDetailsNodeQuery?.result.value?.node as TitleDetail)
		: (titleDetailsFullPathQuery?.result.value?.urlV2.node as TitleDetail);

	return node ?? null;
});

/* LOADING */
// is the title detail page in loading mode for:
// - requests
const isDataLoading = computed(() => titleDetailsFullPathQuery?.loading.value || titleDetailsNodeQuery?.loading.value);

// Used to show skeleton while buybox above the fold is loading to prevent CLS.
// Could be used for similar cases with other components
const isReadyToRender = ref(true);

// for INP improvements:
// we need an immediate change upon userinteraction.
// the biggest user interaction on title detail pages (~40% user interactions) is navigating from title detail to another title detail, and it's the least performant, too.
// this `perceivedLoading` will briefly (0ms) v-if in and out. so the children elements don't take up all of our performance budget.
const perceivedLoading = ref(false);

// is the title detail page in loading mode for:
// - requests
// - async components loading (note: if we don't wait for async component loading, the CLS will increase)
// - artificial perceived performance after a user interaction (for INP reasons)
const isLoading = computed(() => isDataLoading.value || !isReadyToRender.value || perceivedLoading.value);

const isPerceivedLoading = computed(() => !titleDetailNode.value || perceivedLoading.value);

// on language change, we update the titleDetailNode, but we do not update the route path.
// in this case we should prevent the page view event from being sent - the page is not changed.
const preventPageViewEvent = ref(false);

let pageTitleId = '';

const { titleName, titleId, titleObjectId, titleObjectType } = useTitleDetailInfo(titleDetailNode);

// ** SRs Adserver Migration ** //
const detailPageSrConfig = adminConfig?.ad_server?.srs?.placements?.detail_page_sr;
const useAdserverSrMutation = computed(
	() => detailPageSrConfig?.all_countries || detailPageSrConfig?.countries?.includes(languageStore.country)
);

const { sponsoredAd } = useAdserverSrMutation.value
	? useGetSrPlacement({
			pageType: 'VIEW_TITLE_DETAIL',
			placement: SraPlacement.DetailPage,
			adPlacement: AdPlacement.DetailPageSr,
			jwEntityID: () => props.entityId ?? titleId.value,
			isEnabled: () => !isDataLoading.value,
	  })
	: useSendBidId({
			pageType: 'VIEW_TITLE_DETAIL',
			placement: SraPlacement.DetailPage,
			jwEntityID: () => props.entityId ?? titleId.value,
			isEnabled: () => !isDataLoading.value,
	  });

const isListSponsoredAd = computed(() => sponsoredAd.value?.campaign?.node?.__typename === 'GenericTitleList');

const isCampaignWithinSimiliarTitles = computed(() => {
	return sponsoredAd.value?.holdoutGroup == false && sponsoredAd.value?.campaign != null && !isListSponsoredAd.value;
});

function onTitleDetailNodeChange() {
	if (!titleDetailNode.value) return;

	// reset state
	clearTitleContext();

	const isSeason = titleDetailNode.value.__typename === 'Season' && !!titleDetailNode.value.show;
	setTitleContext({
		jwEntityId: titleId.value,
		titleId: titleObjectId.value,
		objectType: titleObjectType.value,
		seasonNumber: isSeason ? titleDetailNode.value.content.seasonNumber : undefined,
		episodeNumber: undefined,
		contentType: titleContentType.value,
	});

	// save last visited titleId for search params
	setLastVisitedTitleDetails({
		titleId: titleId.value,
		titleName: titleName.value,
		isTitleUpcoming: !titleDetailNode.value.content.isReleasedV2,
		fullPath: titleDetailNode.value.content.fullPath,
		titleObjectId: titleDetailNode.value.objectId,
		titleObjectType: titleDetailNode.value.objectType,
	});

	// set permanent audiences
	if (titleDetailNode.value.__typename !== 'Season') {
		setPermanentAudiences(titleDetailNode.value.permanentAudiences);
	}

	if (!titleDetailInModal.value) {
		// set meta
		metaStore.setTitleJsonLD({ title: titleDetailNode.value });

		// make sure the pageview tracking event is added to the pooling on client only
		if (process.client) {
			// When the user changes the country from the BB we should trigger a page view event.
			const newPageId = titleDetailNode.value.id + languageStore.country;
			if (pageTitleId !== newPageId) {
				pageTitleId = newPageId;
				TrackingHelper.trackPage(route.path);
			}
		}

		// ensure the modal state is reset
		trackingStore.setOverridePageState(null);

		// add inLists to the title detail store
		setInLists((titleDetailsUrl.value as AssertByTypename<GetUrlTitleDetailsQuery['urlV2'], 'Url'>)?.inLists ?? []);
	} else {
		// we temporarily update the page_type_context when the TitleDetail is opened in a modal
		trackingStore.setOverridePageState({
			pageType: titleObjectType.value,
			pageViewUUID: generateUUIDv4(),
			pageEntryMode: 'modal',
		});
	}

	// reset optimistic buybox filter
	userStore.setBuyboxFilterTypeOptimistic(null);
}

const head = computed(() => {
	if (titleDetailNode.value == null) return {};

	return {
		meta: [
			{
				vmid: 'og:image',
				property: 'og:image',
				content: JW_CONFIG.IMAGESCALER_URL + titleDetailNode.value.content?.fullPosterUrl,
			},
			{
				vmid: 'og:type',
				property: 'og:type',
				content: titleObjectType.value === ObjectType.Movie ? 'video.movie' : 'video.tv_show',
			},
			{ vmid: 'og:image:width', property: 'og:image:width', content: '718' },
			{ vmid: 'og:image:height', property: 'og:image:height', content: '1024' },
			// CTR_META_DESCRIPTIONS_EXPERIMENTS
			...(ctrExperimentMetaDescriptionV2.value
				? [
						{
							vmid: 'description',
							name: 'description',
							content: ctrExperimentMetaDescriptionV2.value,
						},
						{
							vmid: 'og:description',
							property: 'og:description',
							content: ctrExperimentMetaDescriptionV2.value,
						},
				  ]
				: []),
			// CTR_META_DESCRIPTIONS_EXPERIMENTS

			...(overrideMetaDesc.value
				? [
						{
							vmid: 'description',
							name: 'description',
							content: overrideMetaDesc.value,
						},
						{
							vmid: 'og:description',
							property: 'og:description',
							content: overrideMetaDesc.value,
						},
				  ]
				: []),
		],
	};
});

const promotedOffers = computed(() => {
	const promotedOffers = titleDetailNode.value?.promotedOffers ?? [];

	const promotedOffersWithOverride = getPromotedOffersWithOverride(
		titleDetailNode.value,
		languageStore.country,
		promotedOffers
	);

	return promotedOffersWithOverride.filter(
		// Do not promote JWTV on AVOD titles
		// This is because the CMS doesn't support promotion based on monetization type
		offer => offer.package.shortName !== 'jwt' || offer.monetizationType === MonetizationType.Rent
	);
});

const titleDetailsUrl = computed(() => {
	if (!titleDetailInModal.value) {
		const { node, ...rest } =
			(titleDetailsFullPathQuery?.result.value?.urlV2 as AssertByTypename<
				GetUrlTitleDetailsQuery['urlV2'],
				'Url'
			>) || {};

		return rest;
	}

	// either byFullPath or byNodeId (no if else to not make ts trip up on any optional chaining)
	return titleDetailsNodeQuery?.result.value?.node as AssertByTypename<GetNodeTitleDetailsQuery['node'], 'Url'>;
});

const inLists = computed(() => {
	if (!titleDetailInModal.value) {
		return (titleDetailsUrl.value as AssertByTypename<GetUrlTitleDetailsQuery['urlV2'], 'Url'>)?.inLists ?? [];
	}
	return [];
});

// Provide inLists for child components - these are groups of urls exported by BI for targeting
useProvideInLists(inLists);

const { overrideMetaDesc } = useMetaDescExp(titleDetailNode);
const metaData = computed(() => {
	if (!titleDetailsUrl.value) return undefined;

	return {
		...titleDetailsUrl.value,
		htmlContent: titleDetailsUrl.value?.htmlContentJson || {},
	};
});

watch(
	() => userStore.isLoggedIn,
	() => {
		titleDetailsFullPathQuery?.refetch();
		titleDetailsNodeQuery?.refetch();
	}
);

// when leaving page through modal
onBeforeUnmount(() => {
	if (titleDetailInModal.value) {
		// handle case where title detail modal is closed with ESC key
		trackingStore.setOverridePageState(null);
	}
	onLeaveTitleDetailPage();
});

// when leaving page through route
watch(
	() => routingStore.activeRoute,
	(route: Route | null) => {
		const titleHasChanged =
			route?.name?.includes('app.detail') && titleDetailNode.value?.content?.fullPath !== route?.path;

		if (titleHasChanged) {
			clearAdServerResponse(adServerKey.value);
			scrollToTop();
			// We need to reset title object, to prevent CLS issue;
			clearTitleContext();

			// navigating from (popular or title page) -> title detail page is the most expensive userinteraction.
			perceivedLoading.value = true;

			// using `setTimeout` instead of `$nextTick` here, because `$nextTick` will somehow be merged into the same render #vueoptimisations.
			// that will render the component only once which is more expensive because existing components will be updated instead of torn down and created.
			setTimeout(() => (perceivedLoading.value = false), 0);
		}

		// remove sponsored recommendation from store
		clearAllSponsoredAds();

		if (!route?.name?.includes('app.detail')) {
			onLeaveTitleDetailPage();
		}
	}
);

const isMounted = useMounted();

const JWTV_TVOD_Offer = computed(() =>
	titleDetailNode.value ? getJWTVOffer(titleDetailNode.value.offers, languageStore.country, 'TVOD') : undefined
);
const JWAVT_AVOD_Offer = computed(() =>
	titleDetailNode.value ? getJWTVOffer(titleDetailNode.value.offers, languageStore.country, 'AVOD') : undefined
);

const { clearJWTVQueryParams } = useJWTV();
let isModalOpen = false;

function closeJWTVModalCallback() {
	isModalOpen = false;
	clearJWTVQueryParams();
}

if (process.client) {
	watch(
		() => [routingStore.activeRoute, isMounted.value, JWTV_TVOD_Offer.value, JWAVT_AVOD_Offer.value],
		async () => {
			const pathMatchTitle =
				titleDetailNode.value && routingStore.activeRoute?.path === titleDetailNode.value?.content?.fullPath;

			if (isMounted.value && (JWTV_TVOD_Offer.value || JWAVT_AVOD_Offer.value) && pathMatchTitle) {
				const { packageShortName, linkContextId, expPromoCode, titleSlug, isFallback } = getJWTVQueryParams(
					routingStore.activeRoute?.query ?? {}
				);
				if ((packageShortName === 'jwt' || !!titleSlug) && !isModalOpen) {
					isModalOpen = true;

					const linkContext = linkContextId
						? new SnowplowLinkContext({
								self: linkContextId,
						  })
						: null;

					// if (JWAVT_AVOD_Offer.value) {
					// 	const medialDealId = JWAVT_AVOD_Offer.value.mediaDealId;
					// 	const jwtvContractContext = medialDealId
					// 		? new SnowplowJWTVContext({ contractId: medialDealId })
					// 		: null;
					// 	const additionalContexts = [jwtvContractContext, linkContext].filter(
					// 		Boolean
					// 	) as SnowplowContext[];
					// 	await openJwTvAvodModal({
					// 		modalProps: {
					// 			offer: JWAVT_AVOD_Offer.value,
					// 			title: titleDetailNode.value,
					// 			additionalContexts,
					// 		},
					// 		onClose: closeJWTVModalCallback,
					// 	});
					// } else

					if (JWTV_TVOD_Offer.value) {
						await openJwTvRentalModal({
							modalProps: {
								title: titleDetailNode.value,
								offer: JWTV_TVOD_Offer.value,
								additionalContexts: linkContext ? [transformContextToLinkToContext(linkContext)] : [],
								titleSlug: titleSlug,
								promoCode: expPromoCode,
								isFallback,
							},
							onClose: closeJWTVModalCallback,
						});
					}
				}
			}
		},
		{ immediate: true }
	);
}

watch(
	() => languageStore.language,
	(oldLanguage, newLanguage) => {
		if (oldLanguage !== newLanguage && routingStore.activeRoute?.name?.includes('app.detail')) {
			preventPageViewEvent.value = true;
		}
	}
);

function onLeaveTitleDetailPage() {
	resetPermanentAudiences();
	setSponsoredRecommendation(undefined);
	clearAllSponsoredAds();
	clearInLists();

	// clears modal title only on modal close/switch, or all keys on navigation away from title page
	clearAdServerResponse(titleDetailInModal.value ? adServerKey.value : undefined);

	// when the user leaves the page.
	pageTitleId = '';
}

const upcomingReleaseFromSelectedPackages = computed(() => {
	const userPackages = userStore.settings.providers;

	if (!titleDetailNode.value?.content) return null;

	return (
		titleDetailNode.value.content.upcomingReleases
			?.filter(item => diffInMilliseconds(new Date(item.releaseDate)) > 0)
			.filter(item => (item?.releaseCountDown ?? diffInDays(new Date(item.releaseDate))) < 30)
			.find(item => item.package?.shortName && userPackages.includes(item.package?.shortName)) || null
	);
});

const leavingOfferFromSelectedPackages = computed(() => {
	const userPackages = userStore.settings.providers;

	if (!titleDetailNode.value?.content) return null;

	return (
		titleDetailNode.value.availableTo
			?.filter(item => (item?.availableCountDown ?? diffInDays(new Date(item.availableToDate))) < 30)
			.find(item => userPackages.includes(item.package?.shortName)) || null
	);
});

const titleContentType = computed(() => {
	const availabilityBadgeType = !!upcomingReleaseFromSelectedPackages.value ? 'upcoming' : 'leaving';

	const availabilityBadgeDate =
		upcomingReleaseFromSelectedPackages.value?.releaseDate ??
		leavingOfferFromSelectedPackages.value?.availableToDate ??
		null;

	if (userStore.isLoggedIn && availabilityBadgeDate != null) {
		return availabilityBadgeType === 'upcoming' ? TitleContentType.ComingSoon : TitleContentType.LeavingSoon;
	}

	return undefined;
});

const AVODOffer = computed(() => extractAVODOffer(titleDetailNode.value));

onMounted(async () => {
	setTimeout(() => {
		const query = routingStore.activeRoute?.query!;

		if (AVODOffer.value && query['package'] === 'plc') {
			ModalHelper.openModal(
				PlexPlayerModal,
				{ offer: AVODOffer.value, title: titleDetailNode.value },
				{ cssClass: 'trailer-modal' },
				'',
				true,
				[]
			);
		}
		if (query['package'] === 'otc') {
			const ottOffer =
				titleDetailNode.value?.free?.find(el => el.package.technicalName === 'ottstudioplayer') || null;
			if (ottOffer) {
				ModalHelper.openModal(
					OTTPlayerModal,
					{ offer: ottOffer, title: titleDetailNode.value },
					{ cssClass: 'trailer-modal' },
					'',
					true,
					[]
				);
			}
		}
	});

	await nextTick();
});

// UPDATE_META_DESCRIPTIONS
const ctrExperimentMetaDescriptionV2 = computed(() => {
	if (languageStore.country !== 'DE' || titleDetailNode.value?.content?.fullPath == null) return null;
	const path = titleDetailNode.value.content.fullPath;

	return getRichMetaDescriptions(titleDetailNode.value.content.title, path);
});
// UPDATE_META_DESCRIPTIONS

expose({ head });

return { __sfc: true,PlexPlayerModal, OTTPlayerModal, OldTitleDetail, EPISODE_MAX_LIMIT, props, routingStore, metaStore, constantStore, languageStore, userStore, trackingStore, titleDetailStore, setSponsoredRecommendation, setTitleContext, clearTitleContext, setPermanentAudiences, resetPermanentAudiences, setLastVisitedTitleDetails, setInLists, clearInLists, clearAdServerResponse, adServerKey, route, clearAllSponsoredAds, content, scrollToTop, showNewTitlePage, titleDetailInModal, titleDetailsFullPathQuery, titleDetailsNodeQuery, titleDetailNode, isDataLoading, isReadyToRender, perceivedLoading, isLoading, isPerceivedLoading, preventPageViewEvent, pageTitleId, titleName, titleId, titleObjectId, titleObjectType, detailPageSrConfig, useAdserverSrMutation, sponsoredAd, isListSponsoredAd, isCampaignWithinSimiliarTitles, onTitleDetailNodeChange, head, promotedOffers, titleDetailsUrl, inLists, overrideMetaDesc, metaData, isMounted, JWTV_TVOD_Offer, JWAVT_AVOD_Offer, clearJWTVQueryParams, isModalOpen, closeJWTVModalCallback, onLeaveTitleDetailPage, upcomingReleaseFromSelectedPackages, leavingOfferFromSelectedPackages, titleContentType, AVODOffer, ctrExperimentMetaDescriptionV2, SraPlacement, TitleDetailV1, SponsoredRecommendation, Spinner }
}

})